import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  Chip,
  InputBase,
  Paper,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Otp } from '../../../_common';
import FileCopyLine from '../../../_common/Icons/document/FileCopyLine';
import CloseLineIcon from '../../../_common/Icons/system/CloseLine';
import { setAuthenticatorAppState } from './authenticatorApp.slice';
import { useScreenSize } from '../../../../Hooks/useScreenSize';
import { useLanguage } from '../../../../Hooks';
import { useVerifyTotpSetup } from './hooks/useVerifyTotpSetup';
import { authenticatorAppSnackUtil } from './authenticatorAppSnack.util';

export const AuthenticatorAppSetupDialog = () => {
  const { isMobile } = useScreenSize();
  const { t } = useTranslation('authenticatorApp');
  const { lang } = useLanguage({ i18n: {} });
  const dispatch = useDispatch();
  const { setupDialogOpen, setupConfig } = useSelector(
    (state) => state.authenticatorApp,
  );
  const { handleVerifyTotpSetup } = useVerifyTotpSetup();

  const handleClose = () => {
    dispatch(
      setAuthenticatorAppState({
        setupDialogOpen: false,
        setupConfig: null,
      }),
    );
  };

  const form = useForm({
    defaultValues: {
      otp: '',
    },
  });
  const otpValue = form.watch('otp');

  const handleSubmit = (data) => {
    handleVerifyTotpSetup({
      code: data.otp,
      onSuccess: () => {
        form.reset();
        handleClose();
      },
    });
  };

  if (!setupConfig) {
    return null;
  }

  const { qrCode, secretBase32 } = setupConfig;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(secretBase32);
    authenticatorAppSnackUtil.codeCopied({ lang });
  };

  return (
    <Dialog
      open={setupDialogOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="xs"
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant={isMobile ? 'body2' : 'h6'}>
            {t('setupAuthenticatorApp')}
          </Typography>
          {isMobile && (
            <IconButton onClick={handleClose}>
              <CloseLineIcon />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Stack spacing={1} direction="row" alignItems="center">
              <Chip
                label={t('step1')}
                variant="filled"
                size="small"
                color="primary"
              />
              <Typography variant="subtitle2" align="center" fontWeight="bold">
                {t('scanQrCode')}
              </Typography>
            </Stack>
            <Stack>
              <Typography
                variant="caption"
                align="center"
                color="textSecondary"
                textAlign="left"
              >
                {t('scanQrCodeDescription')}
              </Typography>
            </Stack>
            <Paper>
              <Stack justifyContent="center" alignItems="center" width="100%">
                <img
                  src={qrCode}
                  alt="QR Code"
                  style={{ width: 200, height: 200 }}
                />
                <Divider sx={{ width: '100%' }}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {t('enterCodeManually')}
                  </Typography>
                </Divider>
                <Stack direction="row" p={1} sx={{ width: '100%' }} spacing={1}>
                  <InputBase
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={secretBase32 || ''}
                    readOnly
                    sx={{
                      padding: '2px 4px',
                      borderRadius: 2,
                      backgroundColor: (theme) =>
                        theme.palette.primary.selected,
                    }}
                  />
                  <Button
                    size="small"
                    onClick={handleCopyCode}
                    variant="outlined"
                    sx={{ minWidth: 'auto' }}
                    color="inherit"
                  >
                    <FileCopyLine width={16} />
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
          <Stack spacing={1}>
            <Stack spacing={1} direction="row" alignItems="center">
              <Chip
                label={t('step2')}
                variant="filled"
                size="small"
                color="primary"
              />
              <Typography variant="subtitle2" align="center" fontWeight="bold">
                {t('getVerificationCode')}
              </Typography>
            </Stack>
            <Stack>
              <Typography
                variant="caption"
                align="center"
                color="textSecondary"
                textAlign="left"
              >
                {t('enterVerificationCodeDescription')}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Controller
                name="otp"
                control={form.control}
                render={({ field }) => (
                  <Otp
                    otp={field.value}
                    setOtp={field.onChange}
                    options={{
                      boxSpacing: 2,
                      boxPadding: '10px 0px',
                      placeholder: '0',
                      numeric: true,
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={form.handleSubmit(handleSubmit)}
          disabled={otpValue.length !== 6}
          variant="contained"
        >
          {t('continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
